import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AuthShell } from "@/components/CarScene";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", institution_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/institutions").then(({ data }) => setInstitutions(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.institution_id) return toast.error("Select your institution");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/verify");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell tagline="Join the verified campus network — pool rides, find buddies, never travel alone.">
      <h1 className="font-display font-black text-3xl">Join your campus</h1>
      <p className="text-sm text-white/50 mt-1">Create your verified THIRI account.</p>
      <form onSubmit={submit} className="bg-[#121417] border border-white/10 rounded-2xl p-6 mt-6 space-y-4 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.8)]">
          <Input data-testid="register-name-input" required placeholder="Full name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          <Input data-testid="register-email-input" type="email" required placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          <Input data-testid="register-phone-input" required placeholder="Phone number" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          <Input data-testid="register-password-input" type="password" required minLength={6} placeholder="Password (min 6 chars)"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          <Select value={form.institution_id} onValueChange={(v) => setForm({ ...form, institution_id: v })}>
            <SelectTrigger data-testid="register-institution-select" className="rounded-2xl bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="Select your institution" />
            </SelectTrigger>
            <SelectContent>
              {institutions.map((i) => (
                <SelectItem key={i.id} value={i.id}>{i.name} · {i.type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="register-submit-btn" disabled={busy} type="submit"
            className="w-full rounded-full font-bold bg-[#0E1A13] text-[#22C55E] border border-[#1DAF5A]/50 hover:bg-[#12241A] shadow-[0_8px_32px_hsla(145,72%,40%,0.25)] active:scale-95 transition-all duration-150">
            {busy ? "Creating account…" : "Create account"}
          </Button>
          <div className="flex items-center gap-3 text-xs text-white/40"><span className="flex-1 h-px bg-white/10" />or<span className="flex-1 h-px bg-white/10" /></div>
          <Button data-testid="register-google-btn" type="button" variant="outline" onClick={googleLogin}
            className="w-full rounded-full font-bold bg-transparent border-white/15 text-white hover:bg-white/10 hover:text-white active:scale-95 transition-all duration-150">
            Continue with Google
          </Button>
        </form>
        <p className="text-sm text-center mt-4 text-white/50">
          Already on THIRI? <Link to="/login" data-testid="register-to-login" className="font-bold text-[#22C55E]">Log in</Link>
        </p>
    </AuthShell>
  );
}
